import { LitElement, createRef, css, html, nothing, ref, when } from '@lit'
import styles from '@nova/styles'
import { formatPrice, t } from '@nova/utils'

class NovaMiniCart extends LitElement {
  // createRenderRoot() {
  //   return this; // renders into Light DOM
  // }

  static properties = {
    initialItems: {
      type: Array,
    },
    items: {
      type: Array,
      state: true,
    },
    cartDiscounts: { type: Array, state: true },
    totalDiscount: { type: Number, state: true },
    cartSubtotal: { type: Number, state: true },
    isRefreshing: { type: Boolean, state: true },
    isAsync: {
      type: Boolean,
      attribute: 'is-async',
      converter: (value) => value === "true",
    },
    hideShippingBar: {
      type: Boolean,
      attribute: 'hide-shipping-bar',
      converter: (value) => value === "true",
    }
  }

  get count() {
    const count = this.items.reduce(
      (acc, current) => acc + Number.parseInt(current.quantity),
      0,
    )
    return count
  }

  get subtotal() {
    return this.cartSubtotal
  }

  get formattedSubtotal() {
    return formatPrice(this.cartSubtotal)
  }


  getCompareAtPrice(currentItem) {
    return this.initialItems.find((item) => item.key === currentItem.key)
      ?.original_line_price
  }

  $cartDrawer = createRef()

  constructor() {
    super()

    this.initialItems = []
    this.items = []
    this.totalQty = 0
    this.cartDiscounts = []
    this.totalDiscount = 0
    this.cartSubtotal = 0
    this.isRefreshing = false
  }

  connectedCallback() {
    super.connectedCallback()

    document.addEventListener('nova:cart:open', this._handleOpenCart)
    document.addEventListener('nova:cart:close', this._handleCloseCart)
    document.addEventListener('nova:cart:refresh', this.refresh)
  }

  disconnectedCallback() {
    document.removeEventListener('nova:cart:open', this._handleOpenCart)
    document.removeEventListener('nova:cart:close', this._handleCloseCart)
    document.removeEventListener('nova:cart:refresh', this.refresh)
  }

  firstUpdated() {
    if (this.isAsync) {
      const lineItemsTemplate = this.querySelector(
        '#async-minicart-json',
      )

      this.initialItems = lineItemsTemplate
        ? JSON.parse(lineItemsTemplate.content.textContent.trim())
        : []

      document.dispatchEvent(
        new CustomEvent('nova:cart:open', { bubbles: true }),
      )
    }
  }

  _handleOpenCart = async () => {
    if (!this.$cartDrawer.value) return

    this.$cartDrawer.value.show()

    // 🔑 THIS IS THE MISSING PIECE
    await this.refresh()

    // analytics can stay
    const response = await fetch('/cart.js')
    const cart = await response.json()
    Shopify.analytics.publish("custom_cart_viewed", { data: cart })
  }

  _handleCloseCart = () => {
    this.$cartDrawer.value.hide()
  }

  refresh = async () => {
    if (this._isRefreshing) return
    this._isRefreshing = true
    this.isRefreshing = true

    try {
      const response = await fetch('/cart.js')
      if (!response.ok) throw new Error('Error fetching cart')

      const data = await response.json()

      if (this.initialItems.length) {
        data.items = data.items.map(item => {
          const initItem = this.initialItems.find(i => i.key === item.key)
          if (initItem) Object.assign(item, initItem)
          return item
        })
      }

      this.items = data.items
      this.cartSubtotal = data.total_price
      this.totalDiscount = data.total_discount
      this.cartDiscounts = data.cart_level_discount_applications || []

    } catch (err) {
      console.error('Error fetching cart', err)
    } finally {
      this._isRefreshing = false
      this.isRefreshing = false
    }
  }
  willUpdate(changedProperties) {
    if (changedProperties.has('initialItems')) {
      this.items = this.initialItems
    }
    if (changedProperties.has('items')) {
      document.dispatchEvent(
        new CustomEvent('nova:cart:items-count', {
          detail: { count: this.count },
        }),
      )
    }
  }

  _renderEmptyState = () => {
    return html`
      <div class="empty-state">
        <p>${t('cart.general.empty')}</p>
        <a class="btn" variant="secondary-reverse" href="${__NOVA_GLOBAL__.continueShoppingLink}">
          ${t('cart.general.continue_shopping')}
        </a>
      </div>
    `
  }
  continue_shopping
  _renderItems = () => {
    return html`
      <div class="items">
        ${this.items.map(
          (item) =>
            html`
              <nova-cart-item
                @nova-change=${this.refresh}
                .item=${item}
                .compareAtPrice=${this.getCompareAtPrice(item)}>
              </nova-cart-item>
            `,
        )}
      </div>
      <div slot="footer" class="summary">
      ${this.cartDiscounts.map(discount => html`
            <div class="summary__discount">
              <span>${discount.title}</span>
              <span>- ${formatPrice(discount.total_allocated_amount)}</span>
            </div>
          `)}
        <div class="summary__total">
          <h6>${t('cart.general.subtotal')}<span>(${this.totalQty} item${this.totalQty > 1 ? 's': ''})</span></h6>
          <p class="subtotal">
            ${this.isRefreshing
              ? html`<span class="subtotal__spinner"></span>`
              : this.formattedSubtotal}
          </p>        
        </div>
        <a class='btn js-minicart-checkout' variant='secondary-reverse' href="/checkout">${t('cart.general.checkout')}</a>
        <p class="summary__terms">
          ${t('cart.general.terms')}
        </p>
      </div>
    `
  }

  _renderFreeShippingBar = () => {
    return html`
      ${
        __NOVA_GLOBAL__.showFreeShippingProgressBar === true && __NOVA_GLOBAL__.freeShippingThreshold && !this.hideShippingBar
          ? html`<nova-free-shipping-threshold class="${!this.items.length ? 'hidden': ''}" cart-subtotal="${this.subtotal / 100}"></nova-free-shipping-threshold>`
          : nothing
      }`
  }

  _renderTitle = () => {
    if (this.items.length) {
      this.totalQty = this.items.reduce((total, item) => total += item.quantity, 0)
      return `${t('cart.general.title')} (${this.totalQty})`
    } else return t('cart.general.title')
  }

  render() {
    return html`
      <sl-drawer class="drawer" ${ref(
        this.$cartDrawer,
    )}>
        <span slot="label">${this._renderTitle()}</span>
        ${this._renderFreeShippingBar()}
        ${when(!this.items.length, this._renderEmptyState, this._renderItems)}
      </sl-drawer>
    `
  }

  static styles = [
    styles.flex,
    css`
      :host {
        display: block;
      }
      .discount-input-container{
        display:flex;
      }
      .summary__discount {
        display: flex;
        justify-content: space-between;
        color: var(--nova-color-success);
        font: var(--nova-body-caption);
      }
      .subtotal {
        min-height: 1.2em;
        display: flex;
        align-items: center;
        justify-content: flex-end;
      }
      .subtotal__spinner {
        width: 16px;
        height: 16px;
        border: 2px solid var(--nova-color-grey-300);
        border-top-color: var(--nova-color-grey-700);
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
      }
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
      .drawer {
        --size: 100%;
        --header-spacing: 30px 30px 20px 30px;
        --body-spacing: 0px 30px;
        --footer-spacing: 0;
      }
      .drawer::part(overlay) {
        fill: var(--nova-utility-overlay, rgba(0, 0, 0, 0.5));
      }
      .drawer::part(header) {
        align-items: baseline;
        background-color: var(--nova-color-grey-100);
      }
      .drawer::part(title) {
        font: var(--nova-h5-font);
        letter-spacing: var(--nova-h5-letter-spacing);
        text-transform: capitalize;
        padding: var(--nova-space-lg) var(--nova-space-page-margin);
      }

      .drawer::part(header-actions) {
        padding: 0 15px 0 0;
      }

      .drawer::part(close-button__base) {
        color: black;
        padding: 0;
        font-size: 24px;
      }

      @media screen and (min-width: 1024px) {
        .drawer::part(title) {
          padding: var(--nova-space-md) 15px;
        }
      }

      .empty-state {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100%;

        & p {
          font: var(--nova-h6-font);
          letter-spacing: var(--nova-h6-letter-spacing);
          text-transform: var(--nova-h6-letter-transform);
          color: var(--nova-color-grey-400);
          margin-bottom: 15px;
          margin-top: 0;
        }
      }
      .btn {
        border-width: 1px;
        height: var(--nova-button-height);
        display: inline-flex;
        gap: var(--nova-button-gap);
        flex-shrink: 0;
        justify-content: center;
        align-items: center;
        padding-inline: var(--nova-button-padding-left-right);
        font: var(--nova-body-utility);
        letter-spacing: var(--nova-utility-letter-spacing);
        text-transform: var(--nova-utility-text-transform);
        transition: all var(--nova-button-animation-duration) var(--nova-animation-easing);
        cursor: pointer;
        text-decoration: none;
      }
      .btn[variant="secondary-reverse"] {
        border: 0;
        color: var(--nova-color-background);
        background-color: var(--nova-color-grey-600);
      }

      .btn[variant="secondary-reverse"]:not(:disabled):hover {
        color: var(--nova-color-foreground);
        background-color: var(--nova-color-grey-100);
      }

      .btn[variant="secondary-reverse"]:disabled {
        color: var(--nova-color-background);
        background-color: var(--nova-color-grey-300);
        cursor: default;
      }

      .btn[variant="secondary-reverse"]:focus,
      .btn[variant="secondary-reverse"]:focus-visible {
        color: var(--nova-color-background);
        background-color: var(--nova-color-grey-600);
      }
      .btn[variant="secondary-reverse"]:focus {
        outline: none;
      }
      .btn[variant="secondary-reverse"]:focus-visible {
        outline: var(--nova-color-focus) solid 2px;
      }
      @media screen and (min-width: 480px) {
        .drawer {
          --size: 430px;
        }
      }

      .drawer::part(body) {
        padding: 0;
      }

      .items {
        padding: 0 var(--nova-space-sm);

        @media screen and (min-width: 1024px) {
          padding: 0 var(--nova-space-xsm);
        }

        && nova-cart-item + nova-cart-item {
          border-top: 1px solid var(--nova-color-grey-200);
        }
      }

      .summary {
        background: var(--nova-color-grey-100, #F6F6F6);
        padding: var(--nova-space-sm) var(--nova-space-xs);
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-sm);

        @media screen and (min-width: 1024px) {
          padding: var(--nova-space-md) var(--nova-space-xsm);
          gap: var(--nova-space-xsm);
        }

        & a.btn {
          text-decoration: none;
          &:hover {
            background-color: var(--nova-color-white) !important;
          }
        }
      }
      .summary__total {
        display: flex;
        justify-content: space-between;
        align-items: center;

        & h6 {
          margin: 0;
          font: var(--nova-h6-font);
	        letter-spacing: var(--nova-h6-letter-spacing);
	        text-transform: var(--nova-h6-letter-transform);

          & span {
            font: var(--nova-body-font);
            letter-spacing: var(--nova-body-letter-spacing);
            text-transform: var(--nova-body-text-transform);
            display: inline-block;
            padding-left: 3px;
          }
        }

        & p {
          margin: 0;
        }
      }
      .summary__terms {
        color: var(--nova-color-subdued);
        font: var(--nova-body-caption);
	      letter-spacing: var(--nova-caption-letter-spacing);
	      text-transform: var(--nova-caption-text-transform);
        margin: 0;
      }
      .checkout-disabled {
        pointer-events: none;
        cursor: default;
        background-color: var(--nova-color-grey-400) !important;
      }
      .hidden {
        display: none;
      }
    `,
  ]
}

customElements.define('nova-mini-cart', NovaMiniCart)
